<template>

  <div
    class="kmselect-input"
    :class="[
      disabled ? 'is-disabled' : '',
      expanded ? 'kmselect-input-no-toggle' : '',
      showClearButton ? 'has-clear' : '',
    ]"
    :style="{
      cursor: disabled ? 'not-allowed' : 'default',
      backgroundColor: 'transparent',
    }"
    :role="multiple ? 'group' : undefined"
    :aria-labelledby="multiple ? `${labelId} ${selectedValuesId}` : undefined"
    @click="handleWrapperClick"
  >
    <template v-if="multiple">
      <span
        v-for="option in selectedOptions"
        :key="getOptionValue(option)"
      >
        <slot
          name="chip"
          :option="option"
          :remove="() => $emit('chip-remove', getOptionValue(option))"
        >
          <span :ref="'chip-' + getOptionValue(option)">
            <KChip
              :text="getOptionText(option)"
              :disabled="disabled"
              close
              @close="$emit('chip-remove', getOptionValue(option))"
              @mousedown.native.prevent
            />
          </span>
          <KTooltip
            :reference="'chip-' + getOptionValue(option)"
            :refs="$refs"
            placement="top"
            :text="getOptionText(option)"
          />
        </slot>
      </span>
    </template>

    <input
      :id="inputId"
      ref="inputEl"
      v-model="inputModel"
      class="kmselect-native-input"
      :class="$computedClass(placeholderStyle)"
      type="text"
      :placeholder="selectedOptions.length === 0 ? placeholder : ''"
      :disabled="disabled"
      :maxlength="maxlength"
      v-bind="inputAriaAttrs"
      data-focus="true"
      :style="{
        color: disabled ? 'var(--tokens-textDisabled)' : 'var(--tokens-text)',
        cursor: disabled ? 'not-allowed' : 'text',
      }"
      @keydown="$emit('input-keydown', $event)"
      @focus="$emit('input-focus', $event)"
      @blur="$emit('input-blur', $event)"
    >

    <KIconButton
      v-if="showClearButton"
      size="small"
      icon="clear"
      :ariaLabel="clearAllLabel"
      :tooltip="clearAllLabel"
      tooltipPosition="top"
      :color="
        invalid
          ? 'var(--tokens-error)'
          : focused
            ? 'var(--tokens-primary)'
            : hovered
              ? 'var(--tokens-text)'
              : 'var(--palette-grey-v700)'
      "
      class="kmselect-clear-btn"
      @click.stop="$emit('clear-all')"
      @mousedown.native.prevent
      @keydown.enter.stop
      @keydown.space.stop
    />

    <KIconButton
      v-if="!expanded"
      tabindex="-1"
      size="small"
      :icon="isOpen ? 'dropup' : 'dropdown'"
      :ariaLabel="isOpen ? closeLabel : openLabel"
      :tooltip="isOpen ? closeLabel : openLabel"
      tooltipPosition="top"
      :disabled="disabled"
      :aria-expanded="String(isOpen)"
      :aria-controls="listboxId"
      :color="
        invalid
          ? 'var(--tokens-error)'
          : focused
            ? 'var(--tokens-primary)'
            : !disabled && hovered
              ? 'var(--tokens-text)'
              : 'var(--palette-grey-v700)'
      "
      :style="{
        position: 'absolute',
        right: '4px',
        top: '50%',
        transform: 'translateY(-50%)',
      }"
      @click.stop="$emit('toggle')"
      @mousedown.native.prevent
    />
  </div>

</template>


<script>

  import { ref, computed } from 'vue';
  import KIconButton from '../../../../buttons-and-links/KIconButton';
  import KChip from '../../KChip/index.vue';
  import KTooltip from '../../../../KTooltip/index.vue';

  export default {
    name: 'KMultiSelectInput',

    components: { KIconButton, KChip, KTooltip },

    setup(props, { emit }) {
      const inputEl = ref(null);

      const inputModel = computed({
        get() {
          return props.searchText;
        },
        set(val) {
          emit('update:searchText', val);
        },
      });

      const inputAriaAttrs = computed(() => {
        const attrs = {
          role: 'combobox',
          'aria-expanded': String(props.isOpen),
          'aria-haspopup': 'listbox',
          'aria-controls': props.listboxId,
          'aria-autocomplete': 'list',
          'aria-invalid': props.invalid ? 'true' : 'false',
        };
        const labelledby = [];
        if (props.labelId) labelledby.push(props.labelId);
        if (props.multiple && props.selectedValuesId) labelledby.push(props.selectedValuesId);
        if (labelledby.length) attrs['aria-labelledby'] = labelledby.join(' ');
        if (props.isOpen && props.activeDescendant) {
          attrs['aria-activedescendant'] = props.activeDescendant;
        }
        if (props.invalid && props.errorId) {
          attrs['aria-describedby'] = props.errorId;
        }
        return attrs;
      });

      const showClearButton = computed(
        () => props.clearable && props.selectedOptions.length > 0 && !props.disabled,
      );

      const placeholderStyle = computed(() => ({
        '::placeholder': {
          color: 'var(--tokens-textDisabled)',
        },
      }));

      function focus() {
        inputEl.value?.focus();
      }

      function handleWrapperClick() {
        if (!props.disabled) {
          inputEl.value?.focus();
          if (!props.isOpen) {
            emit('toggle');
          }
        }
      }

      function getOptionText(option) {
        if (!option) return '';
        return option[props.itemText];
      }

      function getOptionValue(option) {
        if (!option) return null;
        return option[props.itemValue];
      }

      return {
        inputEl,
        inputModel,
        inputAriaAttrs,
        showClearButton,
        placeholderStyle,
        // eslint-disable-next-line vue/no-unused-properties
        focus,
        handleWrapperClick,
        getOptionText,
        getOptionValue,
      };
    },

    props: {
      selectedOptions: {
        type: Array,
        required: true,
      },
      searchText: {
        type: String,
        default: '',
      },
      placeholder: {
        type: String,
        default: '',
      },
      listboxId: {
        type: String,
        required: true,
      },
      inputId: {
        type: String,
        required: true,
      },
      errorId: {
        type: String,
        default: null,
      },
      multiple: {
        type: Boolean,
        default: true,
      },
      itemText: {
        type: String,
        default: 'label',
      },
      itemValue: {
        type: String,
        default: 'value',
      },
      clearable: {
        type: Boolean,
        default: false,
      },
      isOpen: {
        type: Boolean,
        default: false,
      },

      expanded: {
        type: Boolean,
        default: false,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      invalid: {
        type: Boolean,
        default: false,
      },
      focused: {
        type: Boolean,
        default: false,
      },
      hovered: {
        type: Boolean,
        default: false,
      },
      clearAllLabel: {
        type: String,
        default: null,
      },
      openLabel: {
        type: String,
        default: null,
      },
      closeLabel: {
        type: String,
        default: null,
      },
      activeDescendant: {
        type: String,
        default: null,
      },
      labelId: {
        type: String,
        default: null,
      },
      selectedValuesId: {
        type: String,
        default: null,
      },
      maxlength: {
        type: Number,
        default: null,
      },
    },
  };

</script>


<style lang="scss" scoped>

  .kmselect-input {
    position: relative;
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    align-items: center;
    min-height: 32px;
    padding-top: 4px;
    padding-right: 40px;
    padding-bottom: 4px;
    padding-left: 10px;
    border: 0;
    outline: none;
  }

  .kmselect-native-input {
    flex: 1 1 80px;
    min-width: 80px;
    height: 28px;
    padding: 0 4px;
    font-size: 16px;
    background: transparent;
    border: 0;
    outline: none;
  }

  .kmselect-clear-btn {
    position: absolute;
    top: 50%;
    right: 36px;
    transform: translateY(-50%);
  }

  // Without the toggle there is no gutter to reserve on the right, beyond
  // whatever the clear button needs.
  .kmselect-input-no-toggle {
    padding-right: 10px;

    &.has-clear {
      padding-right: 36px;
    }

    .kmselect-clear-btn {
      right: 4px;
    }
  }

</style>
